import { useCallback, useMemo } from 'react';
import { useAuth, PessoaConfig, PessoaConfigHomeBg, DEFAULT_PESSOA_CONFIG } from '@/contexts/AuthContext';

/**
 * Hook para gerenciar configurações pessoais do usuário
 *
 * Uso:
 * ```tsx
 * const { config, tema, menuPinado, homeBg, updateConfig, setTema, setMenuPinado, setHomeBg } = usePessoaConfig();
 *
 * // Ler valores
 * const isDark = tema === 'dark';
 * const bgUrl = homeBg.tipo === 'imagem' ? homeBg.valor : null;
 *
 * // Atualizar valores
 * await setTema('dark');
 * await setMenuPinado(true);
 * await setHomeBg({ tipo: 'cor', valor: '#154680' });
 *
 * // Preview ao vivo (sem persistir)
 * setHomeBgPreview({ tipo: 'cor', valor: '#ff0000' });
 * // homeBg agora retorna o preview (effectiveHomeBg)
 * ```
 */
export function usePessoaConfig() {
  const { pessoaConfig, updatePessoaConfig, homeBgPreview, setHomeBgPreview } = useAuth();

  // Valores com defaults
  const config = { ...DEFAULT_PESSOA_CONFIG, ...pessoaConfig };
  const tema = config.tema || 'light';
  const menuPinado = config.menu_pinado || false;
  const savedHomeBg = config.home_bg || { tipo: 'preset' as const, valor: 'default' };
  const notificacoes = useMemo(() => config.notificacoes || { email: true, push: false }, [config.notificacoes]);

  // effectiveHomeBg: preview tem prioridade sobre o valor salvo
  const homeBg = homeBgPreview ?? savedHomeBg;

  // Helpers para atualizar configs específicas
  const setTema = useCallback(async (novoTema: 'light' | 'dark') => {
    return updatePessoaConfig({ tema: novoTema });
  }, [updatePessoaConfig]);

  const setMenuPinado = useCallback(async (pinado: boolean) => {
    return updatePessoaConfig({ menu_pinado: pinado });
  }, [updatePessoaConfig]);

  const setHomeBg = useCallback(async (bg: PessoaConfigHomeBg) => {
    return updatePessoaConfig({ home_bg: bg });
  }, [updatePessoaConfig]);

  const setNotificacoes = useCallback(async (notif: Partial<PessoaConfig['notificacoes']>) => {
    return updatePessoaConfig({
      notificacoes: { ...notificacoes, ...notif }
    });
  }, [updatePessoaConfig, notificacoes]);

  // Função genérica para atualizar qualquer config
  const updateConfig = useCallback(async (newConfig: Partial<PessoaConfig>) => {
    return updatePessoaConfig(newConfig);
  }, [updatePessoaConfig]);

  return {
    // Estado completo
    config,

    // Valores individuais (com defaults)
    tema,
    menuPinado,
    homeBg,       // = effectiveHomeBg (preview ?? saved)
    savedHomeBg,  // valor persistido (sem preview)
    notificacoes,

    // Setters específicos
    setTema,
    setMenuPinado,
    setHomeBg,
    setNotificacoes,

    // Preview do background (live preview sem persistir)
    setHomeBgPreview,

    // Setter genérico
    updateConfig
  };
}

export default usePessoaConfig;
